import {
  useGetOneFacility,
  useUpdateFacility,
} from "#src/components/hooks/adapters/useFacilities";
import useLocalization from "#src/hooks/useLocalization";
import { AssetType, Resources } from "@validereinc/domain";
import { Facilities } from "@validereinc/domain-controllers";
import React from "react";
import AssetEditDialog from "./AssetEditDialog/AssetEditDialog";
import { AssetEditDialogPropTypes } from "./AssetEditDialog/types";
import { useQueryClient } from "@tanstack/react-query";

const { FacilityStatusOptions } = Facilities.view;

const EditFacilityDialog = (props: {
  facilityId?: string;
  defaultPeriod: string;
  onClose: () => void;
  onEdit?: () => void;
}) => {
  const { localize } = useLocalization();
  const queryClient = useQueryClient();

  const { data: assetDetails, isLoading: isLoadingDefaultValues } =
    useGetOneFacility(props.facilityId, {
      period: props.defaultPeriod,
    });
  const updateFacilityMutation = useUpdateFacility({
    onSuccess: () => {
      props.onEdit?.();
      props.onClose();
      queryClient.invalidateQueries({
        queryKey: [Resources.ACTIVITY],
      });
    },
    successMessage: `Successfully updated ${localize("facility")} attribute(s)`,
  });

  const isUpdating = updateFacilityMutation.isLoading;
  const isLoading = isUpdating || isLoadingDefaultValues;

  const primaryFields: AssetEditDialogPropTypes["primaryFields"] = [
    {
      display_name: `${localize("facility")} name`,
      field_name: "name",
      data_type: "string",
      is_required: true,
      is_state_managed: false,
    },
    {
      display_name: "Status",
      field_name: "status",
      data_type: "pick-list",
      is_required: true,
      pick_list_values: FacilityStatusOptions.map((option) => ({
        id: option.value,
        name: option.label,
      })),
      is_state_managed: true,
    },
  ];

  return (
    <AssetEditDialog
      primaryFields={primaryFields}
      assetId={props.facilityId}
      assetType={AssetType.FACILITY}
      dialogProps={{ title: `Edit ${localize("facility")} Attributes` }}
      onClose={() => {
        if (isUpdating) return;
        props.onClose();
      }}
      isLoading={isLoading}
      onUpdate={(newValues) =>
        updateFacilityMutation.mutate({
          id: props.facilityId ?? "",
          data: newValues,
        })
      }
      assetDetails={assetDetails}
      hasGeoPointField
    />
  );
};

export default EditFacilityDialog;
